import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { doc, onSnapshot, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { auth, db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

import {
  FaRegCalendarAlt,
  FaRegUser,
  FaHotel,
  FaFolderMinus,
  FaWhmcs,
  FaWalking,
  FaMoneyCheck,
  FaProjectDiagram,
  FaUserCircle,
  FaChevronDown,
  FaMoneyCheckAlt,
  FaCloudMoon,
  FaBusinessTime,
  FaUserSlash,
  FaRegClock,
  FaClock,
  FaCalendarMinus,
  FaLayerGroup,
  FaFileExport,
  FaUserTie,
  FaGavel,
  FaLock,
  FaUserShield,
  FaHistory,
  FaRegCommentDots,
  FaRobot,
} from "react-icons/fa";
import { MdBusiness } from "react-icons/md";

// Importera logotypen
import luneroLogo from "../images/lunero-logo.png";

const ROLE_LABELS_SV = {
  admin: "Administratör",
  manager: "Chef",
  user: "Användare",
  "super-admin": "Super-admin",
};

const SIDEBAR_WIDTH = 240;

const COLORS = {
  bg: "#f5f6fb",
  sidebarBg: "#ffffff",
  sidebarBorder: "#ececf4",
  primaryText: "#252b42",
  secondaryText: "#8891a8",
  mutedText: "#98a0b4",
  purple: "#6323ef",
  purpleLight: "#8d43ff",
  purpleSoft: "#f4f1ff",
  purpleSoft2: "#f7f4ff",
  purpleBorder: "#ece7ff",
  profileBg: "linear-gradient(180deg, #f8f6ff, #f1edff)",
  green: "#16c79a",
};

const MENU_STORAGE_KEYS = {
  project: "projectMenuOpen",
  cases: "casesMenuOpen",
  payroll: "payrollMenuOpen",
  settings: "settingsMenuOpen",
};

const PROJECT_PATHS = [
  "/projects",
  "/project-groups",
  "/project-overview",
  "/project-details",
];

const CASES_PATHS = ["/overtime-requests", "/absence-requests"];

const PAYROLL_PATHS = [
  "/payroll",
  "/ob-overview",
  "/overtime-overview",
  "/absence-overview",
  "/export-payroll",
];

const SETTINGS_PATHS = ["/business"];

function getStoredBoolean(key, fallback = false) {
  const value = localStorage.getItem(key);
  if (value === null) return fallback;
  return value === "true";
}

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, profile } = useAuth();
  const { activeCompany, loading: companyLoading, timestampEnabled, reportsEnabled, smartAssistanceEnabled, customersAndProjectsEnabled, myCompanyEnabled, canAccessPage } = useCompany();

  const [userData, setUserData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [currentUserInfo, setCurrentUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  const [superAdminMenuOpen, setSuperAdminMenuOpen] = useState(location.pathname.startsWith("/super-admin/"));

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [projectMenuOpen, setProjectMenuOpen] = useState(
    getStoredBoolean(MENU_STORAGE_KEYS.project, false)
  );
  const [casesMenuOpen, setCasesMenuOpen] = useState(
    getStoredBoolean(MENU_STORAGE_KEYS.cases, false)
  );
  const [payrollMenuOpen, setPayrollMenuOpen] = useState(
    getStoredBoolean(MENU_STORAGE_KEYS.payroll, false)
  );
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(
    getStoredBoolean(MENU_STORAGE_KEYS.settings, false)
  );

  // Hämta användarinformation från employed
  const fetchUserInfoFromEmployed = async () => {
    if (!currentUser?.email) return null;
    
    const companyAccountUid = activeCompany?.companyAccountUid;
    if (!companyAccountUid) return null;

    try {
      // Hämta från employed-collection
      const employedRef = collection(db, "users", companyAccountUid, "employed");
      const q = query(employedRef, where("email", "==", currentUser.email.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userDoc = querySnapshot.docs[0];
        const userData = userDoc.data();
        
        let createdAtDate = null;
        if (userData.createdAt) {
          if (typeof userData.createdAt?.toDate === "function") {
            createdAtDate = userData.createdAt.toDate();
          } else if (userData.createdAt instanceof Date) {
            createdAtDate = userData.createdAt;
          } else {
            createdAtDate = new Date(userData.createdAt);
          }
        }
        
        let userName = userData.name || "";
        if (userData.firstName && userData.lastName) {
          userName = `${userData.firstName} ${userData.lastName}`;
        } else if (userData.firstName) {
          userName = userData.firstName;
        } else if (userData.lastName) {
          userName = userData.lastName;
        } else if (userData.displayName) {
          userName = userData.displayName;
        }
        
        return {
          id: userDoc.id,
          name: userName || "Användare",
          firstName: userData.firstName || "",
          lastName: userData.lastName || "",
          role: userData.role || "user",
          email: userData.email || currentUser.email,
          createdAt: createdAtDate,
          personalNumber: userData.personalNumber || "",
          phoneNumber: userData.phoneNumber || "",
          employmentNumber: userData.employmentNumber || "",
          companyName: userData.companyName || activeCompany?.companyName || "",
        };
      }
      
      return null;
    } catch (error) {
      console.error("Fel vid hämtning från employed:", error);
      return null;
    }
  };

  // Hämta användardata med onSnapshot för realtidsuppdateringar
  useEffect(() => {
    if (!currentUser?.email || !activeCompany?.companyAccountUid) {
      setCurrentUserInfo(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    const companyAccountUid = activeCompany.companyAccountUid;
    const employedRef = collection(db, "users", companyAccountUid, "employed");
    const q = query(employedRef, where("email", "==", currentUser.email.toLowerCase().trim()));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (!snapshot.empty) {
          const userDoc = snapshot.docs[0];
          const userData = userDoc.data();
          
          let createdAtDate = null;
          if (userData.createdAt) {
            if (typeof userData.createdAt?.toDate === "function") {
              createdAtDate = userData.createdAt.toDate();
            } else if (userData.createdAt instanceof Date) {
              createdAtDate = userData.createdAt;
            } else {
              createdAtDate = new Date(userData.createdAt);
            }
          }
          
          let userName = userData.name || "";
          if (userData.firstName && userData.lastName) {
            userName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData.firstName) {
            userName = userData.firstName;
          } else if (userData.lastName) {
            userName = userData.lastName;
          } else if (userData.displayName) {
            userName = userData.displayName;
          }
          
          setCurrentUserInfo({
            id: userDoc.id,
            name: userName || "Användare",
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            role: userData.role || "user",
            email: userData.email || currentUser.email,
            createdAt: createdAtDate,
            personalNumber: userData.personalNumber || "",
            phoneNumber: userData.phoneNumber || "",
            employmentNumber: userData.employmentNumber || "",
            companyName: userData.companyName || activeCompany?.companyName || "",
          });
        } else {
          // Om användaren inte finns i employed, försök hitta i huvudusers
          fetchUserInfoFromEmployed().then((info) => {
            if (info) {
              setCurrentUserInfo(info);
            } else {
              // Fallback: använd profile-data
              setCurrentUserInfo({
                name: profile?.name || currentUser?.displayName || "Användare",
                firstName: profile?.firstName || "",
                lastName: profile?.lastName || "",
                role: profile?.role || "user",
                email: currentUser?.email || "",
                createdAt: null,
                companyName: activeCompany?.companyName || "",
              });
            }
          });
        }
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av användardata från employed:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.email, activeCompany?.companyAccountUid, profile, currentUser]);

  // Hämta företagsdata
  useEffect(() => {
    if (!activeCompany?.companyAccountUid) {
      setCompanyData(null);
      return;
    }

    const userRef = doc(db, "users", activeCompany.companyAccountUid);

    const unsubscribe = onSnapshot(
      userRef,
      async (docSnapshot) => {
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const companyId = data.companyId || activeCompany?.companyId;

          if (companyId) {
            const companyRef = doc(db, "companies", companyId);
            const companySnap = await getDoc(companyRef);
            if (companySnap.exists()) {
              setCompanyData(companySnap.data());
            } else {
              setCompanyData(null);
            }
          } else {
            setCompanyData(null);
          }
        } else {
          setCompanyData(null);
        }
      },
      (error) => {
        console.error("Fel vid hämtning av företagsdata:", error);
      }
    );

    return () => unsubscribe();
  }, [activeCompany?.companyAccountUid, activeCompany?.companyId]);

  const resolvedUserName = useMemo(() => {
    if (currentUserInfo?.name && currentUserInfo.name !== "Namn saknas") return currentUserInfo.name;
    if (userData?.name && userData.name !== "Namn saknas") return userData.name;
    return profile?.name || currentUser?.displayName || "Användare";
  }, [currentUserInfo?.name, userData?.name, profile?.name, currentUser?.displayName]);

  const userRole = useMemo(() => {
    if (currentUserInfo?.role) return currentUserInfo.role;
    if (activeCompany?.role) return activeCompany.role;
    return profile?.role || "user";
  }, [currentUserInfo?.role, activeCompany?.role, profile?.role]);

  const userEmail = useMemo(() => {
    return currentUserInfo?.email || currentUser?.email || profile?.email || "e-post@exempel.se";
  }, [currentUserInfo?.email, currentUser?.email, profile?.email]);

  const userCreatedAt = useMemo(() => {
    return currentUserInfo?.createdAt || null;
  }, [currentUserInfo?.createdAt]);

  const companyName = useMemo(() => {
    if (currentUserInfo?.companyName) return currentUserInfo.companyName;
    if (activeCompany?.companyName) return activeCompany.companyName;
    if (companyData?.name) return companyData.name;
    if (companyData?.companyName) return companyData.companyName;
    if (profile?.companyName) return profile.companyName;
    return "Laddar...";
  }, [currentUserInfo?.companyName, activeCompany?.companyName, companyData, profile?.companyName]);

  const user = useMemo(
    () => ({
      name: resolvedUserName,
      firstName: currentUserInfo?.firstName || "",
      lastName: currentUserInfo?.lastName || "",
      email: userEmail,
      role: userRole,
      companyId: activeCompany?.companyId || profile?.companyId || "company-name",
      companyName: companyName,
      createdAt: userCreatedAt,
      personalNumber: currentUserInfo?.personalNumber || "",
      phoneNumber: currentUserInfo?.phoneNumber || "",
      employmentNumber: currentUserInfo?.employmentNumber || "",
    }),
    [
      resolvedUserName,
      currentUserInfo?.firstName,
      currentUserInfo?.lastName,
      userEmail,
      userRole,
      userCreatedAt,
      activeCompany?.companyId,
      companyName,
      profile?.companyId,
      currentUserInfo?.personalNumber,
      currentUserInfo?.phoneNumber,
      currentUserInfo?.employmentNumber,
    ]
  );

  useEffect(() => {
    if (PROJECT_PATHS.some((path) => location.pathname.startsWith(path))) {
      setProjectMenuOpen(true);
      localStorage.setItem(MENU_STORAGE_KEYS.project, "true");
    }

    if (CASES_PATHS.some((path) => location.pathname.startsWith(path))) {
      setCasesMenuOpen(true);
      localStorage.setItem(MENU_STORAGE_KEYS.cases, "true");
    }

    if (PAYROLL_PATHS.some((path) => location.pathname.startsWith(path))) {
      setPayrollMenuOpen(true);
      localStorage.setItem(MENU_STORAGE_KEYS.payroll, "true");
    }

    if (SETTINGS_PATHS.some((path) => location.pathname.startsWith(path))) {
      setSettingsMenuOpen(true);
      localStorage.setItem(MENU_STORAGE_KEYS.settings, "true");
    }
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const isPathActive = (paths) =>
    paths.some((path) => location.pathname.startsWith(path));

  const toggleMenu = (key, value, setter) => {
    const next = !value;
    setter(next);
    localStorage.setItem(key, String(next));
  };

  const handleLogoutClicked = async () => {
    try {
      localStorage.removeItem("selectedCompany");
      sessionStorage.removeItem("selectedCompany");
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const formatCreatedAt = (createdAt) => {
    if (!createdAt) return null;

    try {
      if (typeof createdAt?.toDate === "function") {
        return createdAt.toDate().toLocaleDateString("sv-SE");
      }
      
      if (createdAt instanceof Date) {
        return createdAt.toLocaleDateString("sv-SE");
      }
      
      return new Date(createdAt).toLocaleDateString("sv-SE");
    } catch {
      return null;
    }
  };

  const linkBaseStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    textDecoration: "none",
    padding: "13px 14px",
    borderRadius: "14px",
    color: COLORS.secondaryText,
    background: "transparent",
    border: "none",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: 500,
    transition: "all 0.2s ease",
    boxSizing: "border-box",
  };

  const getLinkStyle = (active) => ({
    ...linkBaseStyle,
    color: active ? "#2b2f42" : COLORS.secondaryText,
    background: active ? COLORS.purpleSoft : "transparent",
    fontWeight: active ? 700 : 500,
  });

  const submenuLinkStyle = (active) => ({
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    padding: "11px 14px 11px 18px",
    marginTop: "6px",
    marginLeft: "14px",
    borderRadius: "12px",
    color: active ? "#5f28ea" : "#8f97ad",
    background: active ? COLORS.purpleSoft2 : "transparent",
    fontSize: "14px",
    fontWeight: active ? 600 : 500,
    boxSizing: "border-box",
  });

  const sectionButtonStyle = (active) => ({
    ...linkBaseStyle,
    color: active ? "#2b2f42" : COLORS.secondaryText,
    background: active ? COLORS.purpleSoft : "transparent",
    fontWeight: active ? 700 : 500,
  });

  const renderChevron = (open) => (
    <FaChevronDown
      style={{
        transform: open ? "rotate(180deg)" : "rotate(0deg)",
        transition: "0.2s",
        fontSize: "12px",
        flexShrink: 0,
      }}
    />
  );

  const projectSectionActive = isPathActive(PROJECT_PATHS);
  const casesSectionActive = isPathActive(CASES_PATHS);
  const payrollSectionActive = isPathActive(PAYROLL_PATHS);
  const settingsSectionActive = isPathActive(SETTINGS_PATHS);

  const isLoading = loading || companyLoading;

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        background: COLORS.bg,
        fontFamily: "Inter, Arial, sans-serif",
        overflow: "hidden",
      }}
    >
      <aside
        style={{
          width: SIDEBAR_WIDTH,
          minWidth: SIDEBAR_WIDTH,
          maxWidth: SIDEBAR_WIDTH,
          flexShrink: 0,
          height: "100vh",
          position: "sticky",
          top: 0,
          background: COLORS.sidebarBg,
          borderRight: `1px solid ${COLORS.sidebarBorder}`,
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "26px 18px 16px",
            borderBottom: "1px solid transparent",
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "24px",
              padding: "6px 8px",
            }}
          >
            <img 
              src={luneroLogo}
              alt="Lunero Logo"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "10px",
                objectFit: "cover",
              }}
            />

            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: "22px",
                  fontWeight: 800,
                  color: COLORS.primaryText,
                  lineHeight: 1,
                }}
              >
                Lunero
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: COLORS.mutedText,
                  marginTop: "4px",
                }}
              >
                Smart Schemaläggning
              </div>
            </div>
          </div>

          {canAccessPage("dashboard") && <Link
            to="/dashboard"
            style={{
              width: "100%",
              textDecoration: "none",
              borderRadius: "14px",
              padding: "15px 16px",
              background: `linear-gradient(135deg, ${COLORS.purpleLight}, ${COLORS.purple})`,
              color: "#fff",
              fontSize: "15px",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              boxShadow: "0 12px 26px rgba(99, 35, 239, 0.22)",
              boxSizing: "border-box",
            }}
          >
            Dashboard
            <FaProjectDiagram size={16} />
          </Link>}
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            padding: "8px 18px 16px",
          }}
        >
          <nav style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
            {activeCompany?.role === "super-admin" && (
              <div>
                <button type="button" aria-expanded={superAdminMenuOpen} aria-controls="super-admin-menu"
                  onClick={() => setSuperAdminMenuOpen((open) => !open)}
                  style={sectionButtonStyle(location.pathname.startsWith("/super-admin/") || superAdminMenuOpen)}>
                  <span style={{ display: "flex", alignItems: "center", gap: 12 }}><FaLock aria-hidden="true" /> Super-admin</span>
                  {renderChevron(superAdminMenuOpen)}
                </button>
                {superAdminMenuOpen && <div id="super-admin-menu">
                  <Link to="/super-admin/permissions" style={submenuLinkStyle(isActive("/super-admin/permissions"))}>
                    <FaUserShield style={{ marginRight: 10, flexShrink: 0 }} />Adminbehörigheter
                  </Link>
                  <Link to="/super-admin/log" style={submenuLinkStyle(isActive("/super-admin/log"))}>
                    <FaHistory style={{ marginRight: 10 }} />Logg
                  </Link>
                  <Link to="/super-admin/my-companies" style={submenuLinkStyle(isActive("/super-admin/my-companies"))}>
                    <MdBusiness style={{ marginRight: 10 }} />Mina företag
                  </Link>
                </div>}
              </div>
            )}
            <Link to="/shifts" style={getLinkStyle(isActive("/shifts"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaRegCalendarAlt />
                Tidsschema
              </span>
            </Link>

            {canAccessPage("employees") && <Link to="/employees" style={getLinkStyle(isActive("/employees"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaRegUser />
                Användare
              </span>
            </Link>}

            {canAccessPage("cases") && <div>
              <button
                onClick={() =>
                  toggleMenu(
                    MENU_STORAGE_KEYS.cases,
                    casesMenuOpen,
                    setCasesMenuOpen
                  )
                }
                style={sectionButtonStyle(casesSectionActive || casesMenuOpen)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <FaRegClock />
                  Ärenden
                </span>
                {renderChevron(casesMenuOpen)}
              </button>

              {casesMenuOpen && (
                <div>
                  <Link
                    to="/overtime-requests"
                    style={submenuLinkStyle(isActive("/overtime-requests"))}
                  >
                    <FaClock style={{ marginRight: "10px" }} />
                    Övertid
                  </Link>

                  <Link
                    to="/absence-requests"
                    style={submenuLinkStyle(isActive("/absence-requests"))}
                  >
                    <FaCalendarMinus style={{ marginRight: "10px" }} />
                    Frånvaro
                  </Link>
                </div>
              )}
            </div>}

            {myCompanyEnabled && canAccessPage("myCompany") && <Link to="/my-company" style={getLinkStyle(isActive("/my-company"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <MdBusiness />
                Mitt företag
              </span>
            </Link>}

            {customersAndProjectsEnabled && canAccessPage("customers") && <Link to="/customers" style={getLinkStyle(isActive("/customers"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaHotel />
                Kunder
              </span>
            </Link>}

            {customersAndProjectsEnabled && canAccessPage("projects") && <div>
              <button
                onClick={() =>
                  toggleMenu(
                    MENU_STORAGE_KEYS.project,
                    projectMenuOpen,
                    setProjectMenuOpen
                  )
                }
                style={sectionButtonStyle(projectSectionActive || projectMenuOpen)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <FaFolderMinus />
                  Projekt
                </span>
                {renderChevron(projectMenuOpen)}
              </button>

              {projectMenuOpen && (
                <div>
                  <Link
                    to="/projects"
                    style={submenuLinkStyle(isActive("/projects"))}
                  >
                    <FaFolderMinus style={{ marginRight: "10px" }} />
                    Projekt
                  </Link>

                  <Link
                    to="/project-groups"
                    style={submenuLinkStyle(isActive("/project-groups"))}
                  >
                    <FaLayerGroup style={{ marginRight: "10px" }} />
                    Projektgrupper
                  </Link>

                  <Link
                    to="/project-overview"
                    style={submenuLinkStyle(isActive("/project-overview"))}
                  >
                    <FaProjectDiagram style={{ marginRight: "10px" }} />
                    Projektöversikt
                  </Link>
                </div>
              )}
            </div>}

            {canAccessPage("payroll") && <div>
              <button
                onClick={() =>
                  toggleMenu(
                    MENU_STORAGE_KEYS.payroll,
                    payrollMenuOpen,
                    setPayrollMenuOpen
                  )
                }
                style={sectionButtonStyle(payrollSectionActive || payrollMenuOpen)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <FaMoneyCheck />
                  Lön
                </span>
                {renderChevron(payrollMenuOpen)}
              </button>

              {payrollMenuOpen && (
                <div>
                  <Link
                    to="/payroll"
                    style={submenuLinkStyle(isActive("/payroll"))}
                  >
                    <FaMoneyCheckAlt style={{ marginRight: "10px" }} />
                    Lönhantering
                  </Link>

                  <Link
                    to="/ob-overview"
                    style={submenuLinkStyle(isActive("/ob-overview"))}
                  >
                    <FaCloudMoon style={{ marginRight: "10px" }} />
                    OB-översikt
                  </Link>

                  <Link
                    to="/overtime-overview"
                    style={submenuLinkStyle(isActive("/overtime-overview"))}
                  >
                    <FaBusinessTime style={{ marginRight: "10px" }} />
                    Övertid-översikt
                  </Link>

                  <Link
                    to="/absence-overview"
                    style={submenuLinkStyle(isActive("/absence-overview"))}
                  >
                    <FaUserSlash style={{ marginRight: "10px" }} />
                    Frånvaro-översikt
                  </Link>

                  <Link
                    to="/export-payroll"
                    style={submenuLinkStyle(isActive("/export-payroll"))}
                  >
                    <FaFileExport style={{ marginRight: "10px" }} />
                    Exportera lön
                  </Link>
                </div>
              )}
            </div>}

            {/* REGLER FLIK - Enkel länk utan undermeny */}
            {canAccessPage("rules") && <Link to="/rules" style={getLinkStyle(isActive("/rules"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaGavel />
                Regler
              </span>
            </Link>}

            {canAccessPage("noticeboard") && <Link to="/noticeboard" style={getLinkStyle(isActive("/noticeboard"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaRegCommentDots />Anslagstavla
              </span>
            </Link>}

            {timestampEnabled && canAccessPage("timestamp") && <Link to="/timestamp" style={getLinkStyle(isActive("/timestamp"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaClock />
                Tidstämpling
              </span>
            </Link>}
            {reportsEnabled && canAccessPage("reports") && <Link to="/reports" style={getLinkStyle(isActive("/reports"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaFileExport />
                Rapporter
              </span>
            </Link>}
            {smartAssistanceEnabled && canAccessPage("smartAssistance") && <Link to="/smart-assistance" style={getLinkStyle(isActive("/smart-assistance"))}>
              <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <FaRobot />
                Smart assistans
              </span>
            </Link>}

            <div>
              <button
                onClick={() =>
                  toggleMenu(
                    MENU_STORAGE_KEYS.settings,
                    settingsMenuOpen,
                    setSettingsMenuOpen
                  )
                }
                style={sectionButtonStyle(settingsSectionActive || settingsMenuOpen)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <FaWhmcs />
                  Inställningar
                </span>
                {renderChevron(settingsMenuOpen)}
              </button>

              {settingsMenuOpen && (
                <div>
                  {canAccessPage("business") && <Link
                    to="/business"
                    style={submenuLinkStyle(isActive("/business"))}
                  >
                    <MdBusiness style={{ marginRight: "10px" }} />
                    Bolagsöversikt
                  </Link>}

                  <button
                    onClick={handleLogoutClicked}
                    style={{
                      ...submenuLinkStyle(false),
                      width: "calc(100% - 14px)",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                      background: "transparent",
                    }}
                  >
                    <FaWalking style={{ marginRight: "10px" }} />
                    Logga ut
                  </button>
                </div>
              )}
            </div>
          </nav>
        </div>

        <div
          style={{
            padding: "16px 18px 20px",
            borderTop: "1px solid #f1f2f7",
            flexShrink: 0,
            background: "#ffffff",
          }}
        >
          <div
            style={{
              background: COLORS.profileBg,
              borderRadius: "18px",
              padding: "16px",
              border: `1px solid ${COLORS.purpleBorder}`,
            }}
          >
            <div
              onClick={() => setProfileMenuOpen((prev) => !prev)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                gap: "12px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: "46px",
                    height: "46px",
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #6c31ed, #8f58ff)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "22px",
                    flexShrink: 0,
                  }}
                >
                  <FaUserCircle />
                </div>

                <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                  <span
                    style={{
                      color: "#2b2f42",
                      fontWeight: 700,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {isLoading ? "Laddar..." : (user.name === "Namn saknas" ? "Användare" : user.name)}
                  </span>
                  <span
                    style={{
                      color: "#8f97ad",
                      fontSize: "13px",
                    }}
                  >
                    {isLoading ? "Laddar..." : (ROLE_LABELS_SV[user.role] || user.role || "Användare")}
                  </span>
                </div>
              </div>

              <FaChevronDown
                style={{
                  color: "#8f97ad",
                  transform: profileMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "0.2s",
                  flexShrink: 0,
                }}
              />
            </div>

            {profileMenuOpen && (
              <div
                style={{
                  marginTop: "14px",
                  paddingTop: "14px",
                  borderTop: "1px solid #e7e2fb",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#9aa1b5",
                      marginBottom: "4px",
                    }}
                  >
                    Företagsnamn
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#2b2f42",
                      fontWeight: 600,
                    }}
                  >
                    {isLoading ? "Laddar..." : user.companyName}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#9aa1b5",
                      marginBottom: "4px",
                    }}
                  >
                    E-post
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#2b2f42",
                      wordBreak: "break-word",
                    }}
                  >
                    {isLoading ? "Laddar..." : user.email}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#9aa1b5",
                      marginBottom: "4px",
                    }}
                  >
                    Roll
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: "#2b2f42",
                      fontWeight: 600,
                    }}
                  >
                    {isLoading ? "Laddar..." : (ROLE_LABELS_SV[user.role] || user.role || "Användare")}
                  </div>
                </div>

                {user.createdAt && !isLoading && (
                  <div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#9aa1b5",
                        marginBottom: "4px",
                      }}
                    >
                      Skapad
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#2b2f42",
                      }}
                    >
                      {formatCreatedAt(user.createdAt)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <main
          style={{
            flex: 1,
            minWidth: 0,
            padding: "28px",
            boxSizing: "border-box",
            overflowY: "auto",
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
